import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ChevronRight, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { formatRelativeTime } from '@/lib/utils/time';

interface Topic {
  id: string;
  title: string;
  category_id: string;
  category_name: string;
  replies_count: number;
  last_reply_at: string;
  author: {
    name: string;
    image: string;
  };
}

const TrendingTopics: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchTrendingTopics = async () => {
      setIsLoading(true);
      try {
        // Fetch trending topics from forum
        const { data, error } = await supabase
          .from('forum_topics')
          .select(`
            id,
            title,
            category_id,
            replies_count,
            last_reply_at,
            author:author_id(id, auth_id, full_name, avatar_url),
            category:category_id(id, name)
          `)
          .order('replies_count', { ascending: false })
          .limit(3);
          
        if (error) throw error;
        
        // Transform data
        const transformedTopics = data.map(topic => ({
          id: topic.id,
          title: topic.title,
          category_id: topic.category_id,
          category_name: topic.category.name,
          replies_count: topic.replies_count,
          last_reply_at: topic.last_reply_at,
          author: {
            name: topic.author.full_name,
            image: topic.author.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'
          }
        }));
        
        setTopics(transformedTopics);
      } catch (error) {
        console.error('Error fetching trending topics:', error);
        // Set fallback topics
        setTopics([
          {
            id: '1',
            title: 'Best tennis courts in London?',
            category_id: 'sports-discussion',
            category_name: 'Sports Discussion',
            replies_count: 24,
            last_reply_at: new Date(Date.now() - 3600000).toISOString(),
            author: {
              name: 'John Smith',
              image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=150&q=80'
            }
          },
          {
            id: '2',
            title: 'Looking for padel partners in Barcelona',
            category_id: 'sports-discussion',
            category_name: 'Sports Discussion',
            replies_count: 18,
            last_reply_at: new Date(Date.now() - 7200000).toISOString(),
            author: {
              name: 'Maria Garcia',
              image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80'
            }
          },
          {
            id: '3',
            title: 'Recommendations for golf clubs in Singapore',
            category_id: 'club-recommendations',
            category_name: 'Club Recommendations',
            replies_count: 15,
            last_reply_at: new Date(Date.now() - 10800000).toISOString(),
            author: {
              name: 'David Chen',
              image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80'
            }
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTrendingTopics();
  }, []);
  
  const handleTopicClick = (topicId: string) => {
    navigate(`/dashboard/forums/topic/${topicId}`);
  };
  
  const handleViewAllClick = () => {
    navigate('/dashboard/forums');
  };
  
  if (isLoading) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg h-20"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (topics.length === 0) return null;
  
  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <MessageSquare className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Trending Discussions</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      <div className="space-y-3">
        {topics.map((topic) => (
          <div
            key={topic.id}
            className="bg-dark-300 rounded-lg p-3 cursor-pointer hover:bg-dark-400 transition-colors"
            onClick={() => handleTopicClick(topic.id)}
          >
            <div className="flex items-start">
              <div className="mr-3 flex-shrink-0">
                <img
                  src={topic.author.image}
                  alt={topic.author.name}
                  className="w-10 h-10 rounded-full object-cover border border-dark-400"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white line-clamp-1 text-sm">{topic.title}</h4>
                <div className="flex items-center text-dark-800 text-xs mt-1">
                  <span className="bg-dark-400 text-dark-800 px-2 py-0.5 rounded-full mr-2">
                    {topic.category_name}
                  </span>
                  <span className="flex items-center mr-2">
                    <Users className="h-3 w-3 mr-1" />
                    {topic.replies_count}
                  </span>
                  <span>
                    {formatRelativeTime(topic.last_reply_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendingTopics;